import { randNumber } from '@ngneat/falso';
import {
  BillingEntityRegistrationNumberType,
  ChartView,
  Gender,
  IBillingEntity,
  IConditionConfiguration,
  IMultiTreatmentConfiguration,
  IPractice,
  IProviderData,
  IQuickChartingConfigurations,
  IRecentlyViewed,
  IStaffer,
  IStafferMetadata,
  IStafferOnlineProfile,
  IStafferSettings,
  IStafferTasks,
  ITeam,
  ITreatmentConfiguration,
  IUser,
  PatientMediaGroupBy,
  PatientMediaSize,
  PatientMediaViewType,
  ServiceTypeModality,
  TimelineDisplayOrientation,
  TimelineDisplayZoom,
} from '@principle-theorem/principle-core/interfaces';
import {
  BaseFirestoreMock,
  INamedDocument,
  Properties,
  uid,
} from '@principle-theorem/shared';
import { MockDocRef, MockNamedDocument } from '@principle-theorem/testing';

export class PractitionerMock
  extends BaseFirestoreMock<Properties<IStaffer>>
  implements Properties<IStaffer>
{
  get tasks(): IStafferTasks {
    return {
      watching: randNumber({ min: 0, max: 10 }),
      assigned: randNumber({ min: 0, max: 10 }),
    };
  }

  get user(): INamedDocument<IUser> {
    return MockNamedDocument<IUser>();
  }

  get teams(): INamedDocument<ITeam>[] {
    const numTeams: number = randNumber({ min: 0, max: 3 });
    return [...Array<INamedDocument<ITeam>>(numTeams)].map(() =>
      MockNamedDocument<ITeam>()
    );
  }

  get quickChartingConfiguration(): IQuickChartingConfigurations {
    return {
      conditions: [{ ref: MockDocRef<IConditionConfiguration>() }],
      treatments: [{ ref: MockDocRef<ITreatmentConfiguration>() }],
      multiTreatments: [{ ref: MockDocRef<IMultiTreatmentConfiguration>() }],
    };
  }

  get providerDetails(): IProviderData[] {
    return [
      {
        practiceRef: MockDocRef<IPractice>(),
        providerNumber: '1234',
        providerModality: ServiceTypeModality.GeneralDentist,
      },
    ];
  }

  get billingEntities(): IBillingEntity[] {
    return [
      {
        uid: uid(),
        isDefault: true,
        displayName: 'ABC Dental',
        entity: {
          registrationNumber: '1234',
          registrationNumberType: BillingEntityRegistrationNumberType.ABN,
          address: '123 Main St',
        },
        deleted: false,
      },
    ];
  }

  get prescriberNumber(): string {
    return '1234';
  }

  get qualifications(): string {
    return 'BMedSci MBBS DCH FRACGP';
  }

  get providerCommission(): number {
    return 0.1;
  }

  get settings(): IStafferSettings {
    return {
      charting: {
        view: ChartView.Adult,
      },
      display: {
        sidebarClosed: false,
      },
      interactions: {
        filters: [],
        tagFilters: [],
      },
      timeline: {
        filteredStaffByPractice: [],
        orientation: TimelineDisplayOrientation.Horizontal,
        zoom: TimelineDisplayZoom.AutoFit,
        thickTracks: false,
        showGridlines: false,
      },
      conversations: {
        showAllPractices: false,
      },
      chats: {
        showAllPractices: false,
      },
      notifications: {
        patientArrivedSoundOverride: undefined,
      },
      treatmentPlan: {
        filters: [],
      },
      patientMedia: {
        viewType: PatientMediaViewType.Grid,
        size: PatientMediaSize.Medium,
        groupBy: PatientMediaGroupBy.None,
      },
      referrals: {
        filters: {
          status: [],
          type: [],
          needsActionOnly: false,
        },
      },
      email: {
        saveAttachmentsToPatientFiles: true,
      },
    };
  }

  get recentlyViewed(): IRecentlyViewed[] {
    return [];
  }

  get onlineProfile(): IStafferOnlineProfile {
    return {
      name: 'Dr. John Doe',
      profileImageURL: 'https://example.com/profile.jpg',
      gender: Gender.NotSpecified,
      title: 'Dentist',
      bio: 'I am a dentist',
    };
  }

  get metadata(): IStafferMetadata {
    return {};
  }
}

export const RECEPTIONISTS: string[] = [
  'Peggy Olson',
  'Pam Beesly',
  'Janine Melnitz',
  'Pepper Potts',
  'Kitty Sanchez',
  'Waylon Smithers',
  'Moneypenny',
];

export const PRACTITIONERS: string[] = [
  'Dr Pierre Fauchard',
  'Dr Edward Angle',
  'Dr Frank Abbott',
  'Dr William Rollins',
  'Dr Lucy Taylor',
];

export const STAFF: string[] = [...RECEPTIONISTS, ...PRACTITIONERS];
